import React, { createContext, useContext, useState, useCallback } from 'react';

interface TabbedSeenUIDsContextType {
  hasSeenUID: (pageId: string, uid: string) => boolean;
  addSeenUID: (pageId: string, uid: string) => void;
}

const TabbedSeenUIDsContext = createContext<TabbedSeenUIDsContextType | undefined>(undefined);

export const TabbedSeenUIDsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [seenUIDsByPage, setSeenUIDsByPage] = useState<Record<string, Set<string>>>({});

  const addSeenUID = useCallback((pageId: string, uid: string) => {
    setSeenUIDsByPage(prev => {
      const newSet = new Set(prev[pageId] || []);
      if (newSet.has(uid)) {
        return prev; // No change needed
      }
      newSet.add(uid);
      return { ...prev, [pageId]: newSet };
    });
  }, []);

  const hasSeenUID = useCallback((pageId: string, uid:string) => {
    return seenUIDsByPage[pageId]?.has(uid) || false;
  }, [seenUIDsByPage]);

  const value = { hasSeenUID, addSeenUID };

  return (
    <TabbedSeenUIDsContext.Provider value={value}>
      {children}
    </TabbedSeenUIDsContext.Provider>
  );
};

export const useTabbedSeenUIDs = () => {
  const context = useContext(TabbedSeenUIDsContext);
  if (context === undefined) {
    throw new Error('useTabbedSeenUIDs must be used within a TabbedSeenUIDsProvider');
  }
  return context;
};
